/**
 * The engine's small DOM contract, in one place: which element is inert while
 * a guide shows, what counts as somebody else's dialog, where a phone sheet
 * must stay clear of, how a target is kept clear of the bars that stay put,
 * and how anything a guide could leave behind is removed.
 *
 * No network, no business control: nothing here clicks, submits or requests.
 */

/** Marks the guide's own card, so it is never mistaken for an application dialog. */
export const CARD_ATTRIBUTE = 'data-guidance-card';

/**
 * Marks a coach's dock, its pill and its target ring: non-modal, never a
 * dialog, never inert — it never owns the keyboard (⌘K still opens Quick Find).
 */
export const DOCK_ATTRIBUTE = 'data-guidance-dock';

/** The Help panel's Sheet (`aria-controls` of the Help entry). */
export const HELP_PANEL_ID = 'guidance-help-panel';

/** The application root the card sits outside of (`index.html`: `<div id="root">`). */
export function applicationRoot(doc: Document = document): HTMLElement | null {
  return doc.getElementById('root');
}

/**
 * Whether a MODAL guide card is on screen — a tour's step, a coach's
 * explanation or hand-over, a paused card — which owns the keyboard. A coach's
 * non-modal dock is not one: while it shows, the page and its shortcuts are the
 * person's (the Quick Find shortcut asks this, CONTRACT with pux-search).
 */
export function isModalGuideOpen(doc: Document = document): boolean {
  return doc.querySelector(`[${CARD_ATTRIBUTE}][aria-modal="true"]`) !== null;
}

/** Marks an `inert` the guide set, so a clean-up never lifts somebody else's. */
const INERT_MARK = 'data-guidance-inert';

function releaseInert(root: HTMLElement): void {
  if (!root.hasAttribute(INERT_MARK)) return;

  root.removeAttribute('inert');
  root.removeAttribute(INERT_MARK);
}

/**
 * Makes the application inert while the card is open — so the spotlit control
 * cannot be pressed by accident and nothing behind the card takes focus — and
 * returns the undo. The undo is idempotent and leaves an `inert` that was
 * already there alone.
 */
export function makeInert(root: HTMLElement | null): () => void {
  if (root === null || root.hasAttribute('inert')) return () => undefined;

  root.setAttribute('inert', '');
  root.setAttribute(INERT_MARK, '');
  let released = false;

  return () => {
    if (released) return;

    released = true;
    releaseInert(root);
  };
}

/**
 * Whether a modal dialog other than the guide's own card is open — a native
 * `<dialog open>` or an `aria-modal` dialog — the Help panel included, while it
 * is open (it closes before a guide starts). A guide never stacks its focus
 * trap on top of one: it steps aside and leaves the dialog alone.
 */
export function foreignDialogOpen(doc: Document = document): boolean {
  const dialogs = doc.querySelectorAll(
    'dialog[open], [role="dialog"][aria-modal="true"], [role="alertdialog"][aria-modal="true"]',
  );

  return [...dialogs].some((dialog) => dialog.closest(`[${CARD_ATTRIBUTE}]`) === null);
}

const FOCUSABLE =
  'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** The card's controls in tab order. */
export function focusablesIn(container: HTMLElement): HTMLElement[] {
  return [...container.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
    (element) => !element.hasAttribute('hidden') && element.closest('[inert]') === null,
  );
}

/**
 * How much of the bottom edge fixed bars take — the phone's bottom navigation
 * and a page's commit bar. A phone sheet docks above them, so a control in one
 * of them can still be seen while it is explained.
 */
export function fixedBottomInset(
  doc: Document = document,
  viewportHeight = window.innerHeight,
): number {
  let inset = 0;

  for (const element of doc.querySelectorAll<HTMLElement>('nav, [data-bottom-action-bar]')) {
    if (element.closest(`[${CARD_ATTRIBUTE}]`) !== null) continue;

    const style = window.getComputedStyle(element);

    if (style.position !== 'fixed' || style.display === 'none' || style.visibility === 'hidden')
      continue;

    const rect = element.getBoundingClientRect();

    if (rect.height > 0 && rect.bottom >= viewportHeight - 2)
      inset = Math.max(inset, viewportHeight - rect.top);
  }

  return Math.max(0, Math.round(inset));
}

/** The room a spotlit target keeps from a bar that stays put (more than the stage padding). */
export const BAR_CLEARANCE = 12;

/** A band of the viewport, in CSS px from its top edge. */
export interface Band {
  top: number;
  bottom: number;
}

interface Viewport {
  width: number;
  height: number;
}

const currentViewport = (): Viewport => ({ width: window.innerWidth, height: window.innerHeight });

/**
 * The band of the viewport that bars which stay put leave free for a target:
 * below every sticky or fixed bar along the top edge (a shell's header) and
 * above every fixed bar along the bottom (the phone's navigation, a commit
 * bar). A bar that holds the target, or that the target holds, does not count:
 * the language switch is not hidden by the header it is in. The same rule the
 * E2E harness's "unobstructed" check measures by (e2e/support/guidance.ts).
 */
export function clearBand(
  target: Element,
  doc: Document = document,
  viewport: Viewport = currentViewport(),
): Band {
  let top = 0;
  let bottom = viewport.height;

  for (const bar of doc.querySelectorAll<HTMLElement>('header, nav, [data-bottom-action-bar]')) {
    if (bar.contains(target) || target.contains(bar)) continue;
    if (bar.closest(`[${CARD_ATTRIBUTE}]`) !== null) continue;

    const style = window.getComputedStyle(bar);

    if (style.position !== 'fixed' && style.position !== 'sticky') continue;
    if (style.display === 'none' || style.visibility === 'hidden') continue;

    const box = bar.getBoundingClientRect();

    if (box.height <= 0 || box.width < viewport.width / 2) continue;

    if (box.top <= 1 && box.bottom < viewport.height / 2) top = Math.max(top, box.bottom);
    else if (style.position === 'fixed' && box.top >= viewport.height / 2)
      bottom = Math.min(bottom, box.top);
  }

  return { top, bottom };
}

/**
 * How far to scroll the page (down is positive) so the target sits in the
 * band, `clearance` from its edges: a target under the top bar comes down to
 * just below it; one under a bottom bar goes up, but never so far that its top
 * goes under the top bar (a target taller than the band keeps its top in view).
 * Zero for a target already clear.
 */
export function revealDelta(
  rect: { top: number; bottom: number },
  band: Band,
  clearance = BAR_CLEARANCE,
): number {
  const top = band.top + clearance;
  const bottom = band.bottom - clearance;

  if (rect.top < top) return rect.top - top;
  if (rect.bottom > bottom) return Math.min(rect.bottom - bottom, rect.top - top);

  return 0;
}

/** Driver.js's own rule: it scrolls a target only when it is not wholly in the viewport. */
function insideViewport(rect: DOMRect, viewport: Viewport): boolean {
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= viewport.height &&
    rect.right <= viewport.width
  );
}

/**
 * Before a target is spotlit, keeps it clear of the bars that stay put — a
 * sticky header, a phone's bottom navigation — in both of the ways it can be
 * brought into view, and returns the undo:
 *
 * - a target wholly inside the viewport, which Driver.js does not scroll, but
 *   under a bar (appx-run1: the operator's Create button, once the long
 *   organisations list had scrolled the page) is scrolled clear at once,
 *   before anything is drawn;
 * - a target Driver.js will scroll into view gets scroll margins of the bars'
 *   size for that scroll, so it does not land under the header either.
 *
 * A target already clear, or with no box (not laid out), is left as it is.
 * Nothing is scrolled after the spotlight is drawn, so the card never shows
 * over a stage that is still moving.
 */
export function keepClearOfBars(
  target: Element,
  viewport: Viewport = currentViewport(),
): () => void {
  const rect = target.getBoundingClientRect();

  if (rect.width <= 0 && rect.height <= 0) return () => undefined;

  const band = clearBand(target, document, viewport);

  if (insideViewport(rect, viewport)) {
    const delta = Math.round(revealDelta(rect, band));

    if (delta !== 0) window.scrollBy({ top: delta, behavior: 'instant' });

    return () => undefined;
  }

  if (!(target instanceof HTMLElement)) return () => undefined;

  const style = target.style;
  const before = [style.scrollMarginTop, style.scrollMarginBottom];

  style.scrollMarginTop = `${Math.round(band.top + BAR_CLEARANCE)}px`;
  style.scrollMarginBottom = `${Math.round(viewport.height - band.bottom + BAR_CLEARANCE)}px`;

  return () => {
    style.scrollMarginTop = before[0];
    style.scrollMarginBottom = before[1];
  };
}

/**
 * Removes anything a guide could leave behind — the overlay's SVG, the
 * spotlight marks, its body classes, an `inert` the guide set on the
 * application root — whatever state the engine ended in. Safe to call at any
 * time, and called whenever a guide ends, however it ends. (The card itself is
 * React's to remove.)
 */
export function clearGuidanceResidue(doc: Document = document): void {
  const root = applicationRoot(doc);

  if (root !== null) releaseInert(root);

  doc
    .querySelectorAll('svg.driver-overlay, #driver-dummy-element')
    .forEach((node) => node.remove());
  doc.body.classList.remove('driver-active', 'driver-fade', 'driver-simple', 'driver-no-scroll');
  doc.body.style.removeProperty('--driver-animation-duration');
  doc
    .querySelectorAll('.driver-active-element, .driver-no-interaction')
    .forEach((element) =>
      element.classList.remove('driver-active-element', 'driver-no-interaction'),
    );
  doc
    .querySelectorAll('.driver-active-element-parent')
    .forEach((element) =>
      element.classList.remove(
        'driver-active-element-parent',
        'driver-active-element-parent-no-scroll',
      ),
    );
}
